// ============================================================================
// T-ALE-15 · EL ORIGEN ÚNICO DE LOS INCIDENTES (R-ALE-01 + R-ALE-02).
//
// ⭐⭐ POR QUÉ ESTO EXISTE Y NO CADA COMPONENTE CONSULTA LO SUYO. La franja y la
// campanita enseñan el MISMO conjunto visto por dos ventanas distintas (la
// franja, solo los `grave`; la campanita, todo lo abierto). Si cada una
// consultara por su lado, dar por visto un incidente en la campanita lo dejaría
// pintado en la franja **hasta 60 s**, y el panel se contradiría a sí mismo en
// pantalla. El estado vive aquí, una sola vez, y las dos lo consumen.
//
// ⛔ Ni `FranjaAlertas` ni `Campanita` reciben `sb`: reciben este estado ya
//    resuelto. No es estilo — es lo que hace IMPOSIBLE la segunda consulta.
//
// Lo que este hook NO hace, a propósito:
//   ⛔ no filtra por inquilino en el navegador — quien reparte es la RLS
//      (`alertas_sel`, 0013:371) con la sesión del usuario;
//   ⛔ no pide el `detalle` cuando quien mira no es Victorix (lo decide
//      `listarAlertasAbiertas`): el motivo técnico no viaja al navegador del
//      dueño;
//   ⛔ no se queda callado si algo falla. Una lista vacía significa «todo bien»;
//      callarse ante un error sería un botón que miente. Y eso vale para las dos
//      cosas que pueden fallar: la consulta (`fallo`) y el «ya lo vi»
//      (`falloAlMarcar`).
// ============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { darAlertaPorVista, listarAlertasAbiertas } from "./datos.ts";
import type { Alerta } from "./tipos.ts";

/** Cada cuánto se vuelve a mirar. Lo pide R-ALE-01: el incidente «desaparece
 *  SOLO» con su `recuperado`, o sea sin que nadie recargue la página. */
export const REFRESCO_MS = 60_000;

export interface EstadoAlertas {
  /** `null` = todavía no se sabe (primera consulta en vuelo o fallo). */
  alertas: Alerta[] | null;
  /** La base tenía más incidentes abiertos de los que caben en el tope. */
  incompleta: boolean;
  /** La consulta falló: hay que DECIRLO, no enseñar una pantalla en calma. */
  fallo: boolean;
  /** Quien mira es de Victorix ⇒ se le pidió (y se le pinta) el `detalle`. */
  esVictorix: boolean;
  /** Da un incidente por visto. Solo puede archivar lo que ya no está pasando:
   *  si sigue vivo, el siguiente grito lo devuelve (lo garantiza la base). */
  marcarVista: (clave: string) => Promise<void>;
  /** Qué clave se está marcando ahora mismo (para no dejar el botón mudo). */
  marcando: string | null;
  /** El «ya lo vi» rebotó. Se enseña: un botón que no hace nada y no lo dice es
   *  peor que un botón que no está. */
  falloAlMarcar: string | null;
}

export function useAlertas(sb: any, esVictorix: boolean): EstadoAlertas {
  const [alertas, setAlertas] = useState<Alerta[] | null>(null);
  const [incompleta, setIncompleta] = useState(false);
  const [fallo, setFallo] = useState(false);
  const [marcando, setMarcando] = useState<string | null>(null);
  const [falloAlMarcar, setFalloAlMarcar] = useState<string | null>(null);

  // ⚠️ El montaje puede desmontarse mientras la consulta está en vuelo. Sin este
  //    testigo, React avisa por consola y —peor— una respuesta vieja podría
  //    pisar un estado nuevo.
  const vivo = useRef(true);

  // ⭐⭐ H-4 · EL ARCHIVO LOCAL, Y NO ES UN LUJO: ES UNA CARRERA REAL. El tick de
  //    60 s puede tener una consulta EN VUELO cuando alguien pulsa «ya lo vi».
  //    Esa consulta salió ANTES de que la base archivara nada, así que trae el
  //    incidente todavía abierto — y al aterrizar pisaría el estado y lo
  //    RESUCITARÍA en pantalla hasta el refresco siguiente. Quien pulsó el botón
  //    concluiría, con razón, que el botón no funciona.
  // ⭐ Se archiva `clave → hasta_id`, que es LA MISMA REGLA que aplica la base
  //    (`v_alertas_abiertas`: `not exists (… av.hasta_id >= u.id)`), no una
  //    segunda regla que pueda desalinearse. ⇒ un grito NUEVO trae un `id`
  //    mayor, no queda tapado por el archivo local, y el incidente vivo
  //    reaparece igual. Un `Set` de claves a secas sí lo taparía.
  // ⚠️ Va en un `ref` y no en el estado: es un dato de sincronización, no algo
  //    que se pinte, y tiene que estar disponible dentro de una respuesta que
  //    llega tarde sin re-suscribir el efecto.
  const archivadas = useRef(new Map<string, number>());
  const filtrar = (lista: Alerta[]) =>
    lista.filter((a) => (archivadas.current.get(a.clave) ?? -1) < a.id);

  // Espejo de la lista pintada, para poder leerla dentro de `marcarVista` sin
  // arrastrar un cierre viejo ni meter efectos dentro de un actualizador de
  // estado (React puede llamarlo dos veces en modo estricto).
  const listaRef = useRef<Alerta[] | null>(null);

  useEffect(() => {
    vivo.current = true;
    const mirar = async () => {
      try {
        const r = await listarAlertasAbiertas(sb, { esVictorix });
        if (!vivo.current) return;
        // ⭐ H-4: toda respuesta pasa por el archivo local, incluida una que
        //    salió antes del «ya lo vi» y llega después. Sin esta línea, el
        //    incidente recién archivado vuelve a la pantalla.
        const vistas = filtrar(r.alertas);
        listaRef.current = vistas;
        setAlertas(vistas);
        setIncompleta(r.incompleta);
        setFallo(false);
      } catch {
        // ⚠️ NO se conservan las alertas viejas al fallar: enseñarlas junto al
        // aviso de fallo diría dos cosas contrarias a la vez. Lo honesto es «no
        // pude comprobar», que es exactamente lo que se sabe.
        if (!vivo.current) return;
        listaRef.current = null;
        setAlertas(null);
        setIncompleta(false);
        setFallo(true);
      }
    };
    void mirar();
    const t = setInterval(() => void mirar(), REFRESCO_MS);
    return () => { vivo.current = false; clearInterval(t); };
  }, [sb, esVictorix]);

  const marcarVista = useCallback(async (clave: string) => {
    setMarcando(clave);
    setFalloAlMarcar(null);
    try {
      const hasta = await darAlertaPorVista(sb, clave);
      if (!vivo.current) return;
      // ⭐ SE QUITA DEL ESTADO AQUÍ, Y POR ESO DESAPARECE DE LAS DOS VENTANAS A
      //    LA VEZ Y SIN RECARGAR. La base ya lo archivó; el refresco siguiente
      //    confirmará lo mismo. ⚠️ Y si el incidente sigue vivo, el próximo
      //    grito lo devuelve solo — eso NO lo decide esta línea, lo decide
      //    `v_alertas_abiertas` (migr 0044).
      // ⚠️ El listón se toma de lo que devolvió la base (`hasta`), y la fila
      //    pintada es solo el respaldo: la función devuelve `null` cuando no
      //    había nada que archivar (clave ya `recuperado`), y ahí lo correcto
      //    sigue siendo quitarlo de la pantalla. `Math.max` porque el listón
      //    LOCAL tampoco puede bajar, igual que el de la base.
      const fila = (listaRef.current ?? []).find((a) => a.clave === clave);
      const tope = Math.max(hasta ?? 0, fila?.id ?? 0, archivadas.current.get(clave) ?? 0);
      archivadas.current.set(clave, tope);
      const vistas = (listaRef.current ?? []).filter((a) => !(a.clave === clave && a.id <= tope));
      listaRef.current = vistas;
      setAlertas((prev) => (prev ? vistas : prev));
    } catch (e: any) {
      if (!vivo.current) return;
      // ⛔ Jamás en silencio. El rebote típico es `42501` («no es tuyo»), que la
      //    función lanza a propósito en vez de devolver un `null` mudo.
      setFalloAlMarcar(
        e?.code === "42501"
          ? "Ese incidente no es de este negocio: no lo puedes dar por visto."
          : "No pude marcarlo como visto. Vuelve a intentarlo.",
      );
    } finally {
      if (vivo.current) setMarcando(null);
    }
  }, [sb]);

  return { alertas, incompleta, fallo, esVictorix, marcarVista, marcando, falloAlMarcar };
}
